import { useEffect, useRef, useState } from 'react';
import { collection, onSnapshot, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../servicios/firebase';

// Genera un "pum" corto con Web Audio API — sin archivos externos
const reproducirSonido = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 520;
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.4, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.18);
  } catch (e) { /* silencioso si no hay soporte */ }
};

// Pide permiso para notificaciones del navegador
const pedirPermiso = async () => {
  if ('Notification' in window && Notification.permission === 'default') {
    await Notification.requestPermission();
  }
};

const notificarNavegador = (ticket) => {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  const esRedondo = ticket.tipoViaje === 'redondo' || ticket.esRedondo;
  const origen = ticket.ruta?.origen || ticket.rutaIda?.origen || '—';
  const destino = ticket.ruta?.destino || ticket.rutaIda?.destino || '—';
  const fecha = ticket.fechaViaje || ticket.fechaIda || '—';
  const pasajeros = Object.keys(ticket.pasajeros || {}).length || ticket.asientosIda?.length || 1;

  new Notification('🎟️ Nueva venta — Ángeles del Puerto', {
    body: `${esRedondo ? '⮂ Redondo' : '→ Sencillo'} • ${origen} ➝ ${destino} • ${fecha} • ${pasajeros} pax`,
    icon: '/favicon.ico',
    tag: ticket.id, // evita duplicados
  });
};

export const useNotificaciones = () => {
  const [notificaciones, setNotificaciones] = useState([]);
  const inicializadoRef = useRef(false);
  const idsVistoRef = useRef(new Set());

  useEffect(() => {
    pedirPermiso();

    // Escucha los últimos 50 tickets ordenados por fecha
    const q = query(
      collection(db, 'tickets'),
      orderBy('fechaCompra', 'desc'),
      limit(50)
    );

    const unsub = onSnapshot(q, (snap) => {
      // Primera carga — solo registrar IDs existentes, no notificar
      if (!inicializadoRef.current) {
        snap.docs.forEach(d => idsVistoRef.current.add(d.id));
        inicializadoRef.current = true;
        return;
      }

      // Detectar tickets NUEVOS (no vistos antes)
      const nuevos = [];
      snap.docChanges().forEach(change => {
        if (change.type === 'added' && !idsVistoRef.current.has(change.doc.id)) {
          idsVistoRef.current.add(change.doc.id);
          const data = change.doc.data();
          const ticket = {
            id: change.doc.id,
            ...data,
            fechaCompra: data.fechaCompra?.toDate?.()?.toISOString() || new Date().toISOString(),
            leido: false,
          };
          nuevos.push(ticket);
        }
      });

      if (nuevos.length > 0) {
        reproducirSonido();
        nuevos.forEach(t => notificarNavegador(t));
        setNotificaciones(prev => [...nuevos, ...prev]);
      }
    });

    return () => unsub();
  }, []);

  const marcarLeido = (id) => {
    setNotificaciones(prev => prev.map(n => n.id === id ? { ...n, leido: true } : n));
  };

  const marcarTodosLeidos = () => {
    setNotificaciones(prev => prev.map(n => ({ ...n, leido: true })));
  };

  const limpiarTodas = () => setNotificaciones([]);

  const noLeidos = notificaciones.filter(n => !n.leido).length;

  return { notificaciones, noLeidos, marcarLeido, marcarTodosLeidos, limpiarTodas };
};